import { Link } from 'react-router-dom';
import { MapPin, ChevronRight, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { Centre } from '@/api/centresApi';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { coverImage, resolveDefaultImages } from '@/utils/centreImages';
import { centreRating } from '@/utils/centreStats';
import { resolveCentreLocality } from '@/utils/seoPaths';

export interface CentreCardProps {
  centre: Centre;
  /** Distance in km from the viewer or the locality being browsed. */
  distance?: number | null;
  /** Where the card links. Defaults to the clinic page. */
  to?: string;
  /** Router state forwarded on click (the directory passes its search back). */
  state?: unknown;
}

/**
 * The clinic card. Single source of truth for /centres and every SEO location
 * page, so the two surfaces cannot drift apart.
 */
export function CentreCard({ centre, distance, to, state }: CentreCardProps) {
  const siteConfig = useSiteConfig();
  const defaults = resolveDefaultImages(siteConfig.default_centre_image);
  const fallback = defaults[0];
  const img = coverImage(centre, defaults);
  const rating = centreRating(centre);
  // Alt text names the place, not just the clinic: "X - physiotherapy clinic in Sector 62, Noida".
  const locality = resolveCentreLocality(centre);
  const place = locality
    ? `${locality.sector.displayName}, ${locality.city.name}`
    : centre.city || '';
  const alt = place
    ? `${centre.name} - physiotherapy clinic in ${place}`
    : `${centre.name} - physiotherapy clinic`;
  // Centre-added extras aren't catalog services, so they don't earn a chip.
  const realServices = (centre.services ?? []).filter((s) => s.type !== 'custom');

  return (
    <Link
      to={to ?? `/centres/${centre.id}`}
      state={state}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border bg-card shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--sc-teal-mid)] hover:shadow-[0_8px_24px_rgba(15,110,86,0.10)]"
    >
      <div className="relative aspect-[3/2] w-full overflow-hidden bg-muted/40">
        {img ? (
          <img
            src={img}
            alt={alt}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
            onError={(e) => {
              const el = e.currentTarget;
              if (fallback && el.src !== fallback) el.src = fallback;
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-primary/10 text-primary">
            <MapPin className="h-8 w-8" />
          </div>
        )}
        <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-card/95 px-2 py-1 shadow-sm backdrop-blur">
          <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
          <span className="text-xs font-semibold text-foreground">
            {rating !== null ? rating.toFixed(1) : 'New'}
          </span>
        </span>
        {distance != null && (
          <span className="absolute left-3 top-3 rounded-full bg-primary/90 px-2 py-1 text-xs font-medium text-primary-foreground backdrop-blur">
            {distance.toFixed(1)} km
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <p className="line-clamp-1 font-semibold text-foreground">{centre.name}</p>
        <p className="mt-1 flex items-start gap-1.5 text-sm text-muted-foreground">
          <MapPin className="mt-0.5 size-3.5 shrink-0 text-[var(--sc-hint)]" />
          <span className="line-clamp-2">{centre.address}</span>
        </p>
        {realServices.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {realServices.slice(0, 3).map((s) => (
              <Badge key={s.name} variant="secondary" className="text-xs">
                {s.name}
              </Badge>
            ))}
            {realServices.length > 3 && (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                +{realServices.length - 3} more
              </Badge>
            )}
          </div>
        )}
        <div className="mt-auto flex items-center justify-between pt-4 text-sm font-semibold text-primary">
          <span>View clinic</span>
          <ChevronRight className="size-4 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </Link>
  );
}

/** The directory grid. Same column rhythm everywhere clinics are listed. */
export function CentreGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
  );
}
